import React, { PureComponent } from 'react'
import styled from '@emotion/styled'
import {
	EMBED_HELPER_SCRIPT_URL,
	CNF_EMBED_HELPER_SCRIPT_URL_MAP,
	EMBED_PRODUCTS,
	EMBED_URL,
	EMBED_URL_WITH_OVERRIDE,
	HELPER_ID,
	MIGRATED_CNF_EMBED_COMPONENTS_MAP
} from 'parameters/index'
import { handleEmbedEvent } from 'components/vendor/MultiProductWidget/eventMapper'
import { buildEmbedParameters } from 'components/vendor/MultiProductWidget/parameterBuilder'
import { addDDTiming } from 'services/AnalyticsLite/Datadog/events'
import { loadScript } from 'lib/scripts/lazyLoadScript'
import Logger from 'services/Logger'
import { DuplicateError } from 'lib/errors'
import { Stack } from 'components/layout/primitives'
import { TextLineSkeleton, TitleSkeleton } from 'components/patterns/Skeleton'

interface Props {
	product: string
	partner: string
	step?: string
	isExplorePage?: boolean
	override?: Record<string, string>
	versionOverride?: string
	isCnfEmbedExperience?: boolean
}

interface State {
	iFrameSrc?: string | null
	queryParameters?: Record<string, unknown>
	showSkeleton: boolean
}

class MultiProductWidget extends PureComponent<Props, State> {
	state: State = {
		showSkeleton: true,
		iFrameSrc: null,
		queryParameters: {}
	}
	componentDidMount(): void {
		const { versionOverride, product, partner, step, override, isCnfEmbedExperience } = this.props
		const migratedEmbedUrl = CNF_EMBED_HELPER_SCRIPT_URL_MAP[product]
		const embedURL = isCnfEmbedExperience && migratedEmbedUrl ? migratedEmbedUrl : EMBED_HELPER_SCRIPT_URL
		loadScript(embedURL, 'async', HELPER_ID)
			.then(() => addDDTiming('Helper Loaded'))
			.catch(e => {
				if (!(e instanceof DuplicateError)) {
					Logger.error(e)
				}
			})
		const stepObj = step ? { step } : {}
		const params = buildEmbedParameters({ partner, product, config: { ...stepObj, ...override } })
		this.setState({ iFrameSrc: `${EMBED_URL_WITH_OVERRIDE(versionOverride)}?${params}` })
		window.addEventListener('message', handleEmbedEvent, false)
		addDDTiming('embed_mount')
	}

	componentWillUnmount(): void {
		window.removeEventListener('message', handleEmbedEvent)
	}

	onFrameLoad = (): void => {
		addDDTiming('embed_onload')
		this.setState(state => ({ ...state, showSkeleton: false }))
	}

	fallbackToBaseURL = () => {
		const params = this.state.iFrameSrc?.split('?')[1]
		if (params) {
			this.setState(prev => ({
				...prev,
				iFrameSrc: `${EMBED_URL}?${params}`
			}))
		}
	}

	render(): JSX.Element {
		const { product, partner, isCnfEmbedExperience } = this.props
		const { iFrameSrc } = this.state
		const MigratedComponent = isCnfEmbedExperience && MIGRATED_CNF_EMBED_COMPONENTS_MAP[product]

		return (
			<IframeWrapper product={product} partner={partner}>
				{!MigratedComponent && (
					<Skeleton visible={this.state.showSkeleton}>
						<Stack style={{ alignItems: 'center' }} space="biggest">
							<TextLineSkeleton />
							<TitleSkeleton />
						</Stack>
					</Skeleton>
				)}
				{MigratedComponent ? (
					<MigratedComponent />
				) : (
					<iframe
						id="iFrameResizer0"
						src={iFrameSrc || ''}
						scrolling="no"
						onError={this.fallbackToBaseURL}
						onLoad={this.onFrameLoad}
						style={{
							border: 'none',
							width: '100%',
							maxWidth: '100%',
							position: 'relative',
							zIndex: 2
						}}
						className="even-iframe"
						data-testid="iframe"
					/>
				)}
			</IframeWrapper>
		)
	}
}

export default MultiProductWidget

const handleIframeSize = (product: string): string => {
	switch (product) {
		case EMBED_PRODUCTS.LOAN:
			return '720px'

		case EMBED_PRODUCTS.CREDIT_CARD:
			return '720px'

		case EMBED_PRODUCTS.DEPOSITS:
			return '500px'

		case EMBED_PRODUCTS.LIFE_INSURANCE:
			return '700px'

		default:
			return '690px'
	}
}

interface IframeWrapperProps {
	product: string
	partner: string
}

const Skeleton = styled.div<{ visible: boolean }>`
	opacity: ${({ visible }) => (visible ? '100%' : '0%')};
	position: absolute;
	padding: 50px;
	background: linear-gradient(
		180deg,
		rgba(255, 255, 255, 1) 0%,
		rgba(255, 255, 255, 1) 61%,
		rgba(255, 255, 255, 0) 100%
	);
	max-width: 750px;
	width: 100%;
	margin: 0 auto;
	border-radius: 4px;
	height: 200px;
	z-index: 1;
	left: 50%;
	transition: opacity 1s;
	transform: translateX(-50%);
	top: 0;
`

const IframeWrapper = styled.div<IframeWrapperProps>`
	position: relative;
	padding-bottom: 40px;
	min-height: ${({ product }): string => handleIframeSize(product)};
`
